import { Combobox, Transition } from "@headlessui/react";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import SearchIcon from "src/assets/down-arrow.svg";
import { cn, getValueFromNestedObject } from "src/utils/utils";
import Checkbox from "./checkbox.component";

interface AutocompletePropsAsync {
    placeholder: string;
    inputClassName?: string;
    dropdownClassName?: string;
    onSelectionChange: any;
    suggestionList: Array<any>;
    displayKey: string;
    uniqueKey?: string;
    disabled?: boolean;
    currentSelected?: Array<any>;
    defaultValue?: Array<any>;
    getAllSelectedValues?: (value: any[]) => void;
    controlledValue?: any[];
    inputRef?: React.RefObject<HTMLInputElement>;
    asyncListFunction?: (query?: string, page?: number) => Promise<any>;
    isAsyncQuery?: boolean;
    showSecondaryDisplayKey?: boolean;
    secondaryDisplayKey?: string;
    additionalDisplayKey?: boolean;
    additionalDisplayKeySearch?: string;
    showSelectAll?: boolean;
}

const MultiAutocompleteAsync: FC<AutocompletePropsAsync> = ({
    placeholder,
    inputClassName,
    dropdownClassName,
    suggestionList,
    displayKey,
    uniqueKey,
    disabled,
    defaultValue,
    onSelectionChange,
    controlledValue,
    inputRef,
    asyncListFunction,
    isAsyncQuery = true,
    showSecondaryDisplayKey = false,
    secondaryDisplayKey,
    additionalDisplayKey = false,
    additionalDisplayKeySearch,
    showSelectAll = false,
}) => {
    const [selectedValues, setSelectedValues] = useState<any[]>(defaultValue || []);
    const [asyncList, setAsyncList] = useState<any[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [query, setQuery] = useState<string>("");

    useEffect(() => {
        if (asyncListFunction && isAsyncQuery) {
            setIsFetching(true);
            asyncListFunction(query)
                .then((res: any) => {
                    if (Array.isArray(res)) {
                        setAsyncList(res);
                    } else if (res?.data && Array.isArray(res.data)) {
                        setAsyncList(res.data);
                    } else if (res?.data && Array.isArray(res?.data?.data)) {
                        setAsyncList(res.data.data);
                    } else {
                        setAsyncList([]);
                    }
                })
                .catch((err: any) => console.log(err))
                .finally(() => setIsFetching(false));
        } else if (asyncListFunction && !isAsyncQuery) {
            setIsFetching(true);
            asyncListFunction()
                .then((res: any) => {
                    if (Array.isArray(res)) {
                        setAsyncList(res);
                    } else if (res?.data && Array.isArray(res.data)) {
                        setAsyncList(res.data);
                    } else if (res?.data && Array.isArray(res?.data?.data)) {
                        setAsyncList(res.data.data);
                    } else {
                        setAsyncList([]);
                    }
                })
                .catch((err: any) => console.log(err))
                .finally(() => setIsFetching(false));
        }
    }, [query]); // or [], or [asyncListFunction, isAsyncQuery, query]


    const filteredQuery =
        query === ""
            ? asyncListFunction ? asyncList : suggestionList
            : (asyncListFunction ? asyncList : suggestionList).filter(
                (item) => {
                    const searchValue = query?.toString()?.toLowerCase().replace(/\s+/g, "");

                    const matches = (key?: string) => {
                        return key &&
                            getValueFromNestedObject(item, key)
                                ?.toString()
                                ?.toLowerCase()
                                .replace(/\s+/g, "")
                                .includes(searchValue as string);
                    };

                    return additionalDisplayKeySearch && additionalDisplayKey
                        ? (
                            matches(displayKey) ||
                            matches(additionalDisplayKeySearch)
                        )
                        : matches(displayKey);
                }
            );

    let queryTimer: any = null;

    const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (asyncListFunction) {
            clearTimeout(queryTimer);

            queryTimer = setTimeout(() => {
                setQuery(event.target.value);
            }, 500);
        } else setQuery(event.target.value);

        // if (event.target.value === "") {
        //     setSelectedValues([]);
        // }
    };

    // const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    //     setQuery(event.target.value);

    //     if (event.target.value === "") {
    //         setSelectedValues([]);
    //     }
    // };

    // const fetchMoreData = async () => {
    //     if (!asyncListFunction) return;
    //     if (isFetching) return;
    //     setIsFetching(true);
    //     try {
    //         const res = await asyncListFunction(query, currentPage);
    //         // Append new results to asyncList
    //         if (Array.isArray(res)) {
    //             setAsyncList((prev) => [...prev, ...res]);
    //         } else if (res?.data && Array.isArray(res.data)) {
    //             setAsyncList((prev) => [...prev, ...res.data]);
    //         } else if (res?.data && Array.isArray(res?.data?.data)) {
    //             setAsyncList((prev) => [...prev, ...res.data.data]);
    //         }
    //         setCurrentPage((prev) => prev + 1);
    //         setCurrentDataSize((prev) => prev + 15);
    //     } finally {
    //         setIsFetching(false);
    //     }
    // };

    const handleServerSideApiCall = () => {
        if (asyncListFunction && isAsyncQuery) {
            setIsFetching(true);
            asyncListFunction(query)
                .then((res: any) => {
                    if (Array.isArray(res)) {
                        setAsyncList(res);
                    } else if (res?.data && Array.isArray(res.data)) {
                        setAsyncList(res.data);
                    } else if (res?.data && Array.isArray(res?.data?.data)) {
                        setAsyncList(res.data.data);
                    } else {
                        setAsyncList([]);
                    }
                })
                .catch((err: any) => console.log(err))
                .finally(() => setIsFetching(false));
        } else if (asyncListFunction && !isAsyncQuery) {
            setIsFetching(true);
            asyncListFunction()
                .then((res: any) => {
                    if (Array.isArray(res)) {
                        setAsyncList(res);
                    } else if (res?.data && Array.isArray(res.data)) {
                        setAsyncList(res.data);
                    } else if (res?.data && Array.isArray(res?.data?.data)) {
                        setAsyncList(res.data.data);
                    } else {
                        setAsyncList([]);
                    }
                })
                .catch((err: any) => console.log(err))
                .finally(() => setIsFetching(false));
        }
    };


    useEffect(() => {
        if (controlledValue == null) {
            setSelectedValues([]);
        }
        if (controlledValue && Array.isArray(controlledValue)) {
            const transformedData = controlledValue?.map((item: any) => getValueFromNestedObject(item, displayKey));
            setSelectedValues(transformedData || []);
        }
    }, [controlledValue]);

    const selectHandler = (value: any) => {
        try {
            // check if it already selected or not
            const isPresent = selectedValues?.find(
                (item) => item?.toString()?.toLocaleLowerCase()?.trim() === getValueFromNestedObject(value, displayKey)?.toString()?.toLocaleLowerCase()?.trim()
            );

            if (isPresent) {
                // if already selected remove it from the list
                const newList = selectedValues?.filter((item) => item !== getValueFromNestedObject(value, displayKey));
                onSelectionChange(value, "remove");
                setSelectedValues(newList);
            } else {
                // otherwise add it to the list
                setSelectedValues((prev) => [...prev, getValueFromNestedObject(value, displayKey)]);
                onSelectionChange(value, "add");
            }
        } catch (error) {
            setSelectedValues([]);
            onSelectionChange("", "reset");
        }
    };

    const selectAllHandler = (checked: boolean) => {
        const allItems = filteredQuery.filter((item) => !item?.disabled);
        if (checked) {
            const values = allItems.map((item) => getValueFromNestedObject(item, displayKey));
            setSelectedValues(values);
            // allItems.forEach(item => onSelectionChange(item, "add"));
            onSelectionChange(allItems, "bulk-add");
        } else {
            setSelectedValues([]);
            // allItems.forEach(item => onSelectionChange(item, "remove"));
            onSelectionChange(allItems, "bulk-remove");
        }
    };

    const isAllSelected =
        filteredQuery.filter((item) => !item?.disabled).length > 0 &&
        filteredQuery
            .filter((item) => !item?.disabled)
            .every((item) =>
                selectedValues.includes(getValueFromNestedObject(item, displayKey))
            );



    return (
        <Combobox value={selectedValues} disabled={disabled} nullable multiple>
            <div className="relative">
                <Combobox.Input
                    ref={inputRef}
                    className={`border w-[12.375rem] rounded-lg  focus:bg-white focus:outline-none text-sm line-height-normal font-normal text-[#1E1E1E] px-6 py-2 ${inputClassName}`}
                    onChange={inputChangeHandler}
                    placeholder={placeholder}
                    autoComplete="off"
                    displayValue={(selectedValues: any[]) => selectedValues?.join(", ")}
                />
                <Combobox.Button onClick={handleServerSideApiCall} className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <img src={SearchIcon} alt="search icon" />
                </Combobox.Button>
                <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery("")}
                >
                    <Combobox.Options
                        className={`bg-white border absolute mt-1 max-h-60 w-full overflow-y-auto rounded-md  py-2 text-base shadow-lg   focus:outline-none sm:text-sm z-10 ${dropdownClassName}`}
                    >
                        {isFetching && (
                            <div className="flex justify-center my-5">
                                <span>Loading...</span>
                            </div>
                        )}
                        {!isFetching && query && !filteredQuery.length && <p className="ml-2">Not found...</p>}
                        {showSelectAll && !isFetching && filteredQuery.length > 0 && (
                            <div
                                className="px-2 py-3 border-b cursor-pointer flex justify-between items-center font-semibold"
                                onClick={() => selectAllHandler(!isAllSelected)}
                            >
                                Select All
                                <Checkbox
                                    checked={isAllSelected}
                                    onChange={(e: any) => selectAllHandler(e.target.checked)}
                                />
                            </div>
                        )}
                        {filteredQuery?.slice(0, 50).map((item) => (
                            <Combobox.Option
                                disabled={item?.disabled}
                                key={uniqueKey ? item[uniqueKey] : item.id}
                                value={getValueFromNestedObject(item, displayKey)}
                            >
                                {({ active, selected }) => (
                                    <div
                                        onClick={() => {
                                            if (item?.disabled) {
                                                return;
                                            }
                                            selectHandler(item);
                                        }}
                                        className={cn(
                                            `${active ? "bg-purple-50 text-purple-700" : ""
                                            }  px-2 py-3 cursor-pointer flex justify-between items-cen ter gap-3 font-semibold
                      ${item?.disabled ? "bg-gray-100" : ""}`
                                        )}
                                    >
                                        {getValueFromNestedObject(item, displayKey) + (showSecondaryDisplayKey && secondaryDisplayKey ? " - " + getValueFromNestedObject(item, secondaryDisplayKey) : "")}
                                        <Checkbox checked={selected} onChange={() => { }} disabled={item?.disabled} />
                                    </div>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default MultiAutocompleteAsync;
